import { http } from '@/shared/api/api-client';
import type { PagedResponse } from '@/shared/api/api-types';

import type {
  BrandDetails,
  BrandListItem,
  BrandListParams,
  BrandOrderItem,
  DenominationOrderItem,
  DenominationSort,
  CatalogueDeletionCheck,
  CatalogueImpact,
  CatalogueImpactRequest,
  CreateBrandPayload,
  CreateDenominationPayload,
  DenominationPayload,
  UpdateBrandPayload,
} from '../types/brand.types';
import { DEFAULT_DENOMINATION_SORT } from '../types/brand.types';

/**
 * Brand HTTP calls — all against the Admin API (UT system
 * `AdminApis/Catalogue`), which enforces permissions and country scope and
 * audits every change. One named function per endpoint (§12).
 */

/** The Admin API's page shape; `totalCount` maps to the app's `totalItems`. */
interface AdminPage<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

function toPagedResponse<T>(page: AdminPage<T>): PagedResponse<T> {
  return {
    items: page.items,
    page: page.page,
    pageSize: page.pageSize,
    totalItems: page.totalCount,
    totalPages: page.totalPages,
  };
}

/** ASP.NET binds arrays from repeated keys (`countryIds=1&countryIds=36`). */
const repeatArrayKeys = { indexes: null } as const;

/** The list's sort keys, mapped to the Admin API's whitelist. */
const SORTS: Record<string, string> = {
  name: 'name',
  sortOrder: 'position',
  denominationCount: 'denominations',
};

/** Paged, searched, filtered and sorted on the server. */
export async function getBrands(
  params: BrandListParams,
  signal?: AbortSignal,
): Promise<PagedResponse<BrandListItem>> {
  const page = await http.get<AdminPage<BrandListItem>>('/admin/brands', {
    params: {
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
      isActive: params.isActive,
      categoryId: params.categoryId,
      countryId: params.countryId,
      sort: (params.sort && SORTS[params.sort]) || 'name',
      descending: params.direction === 'desc',
    },
    signal,
  });
  return toPagedResponse(page);
}

/**
 * Brand Details: the brand, its regions and every denomination with its
 * state in each country. `countryIds` defaults (server-side) to every country
 * in the staff member's scope.
 */
export async function getBrandDetails(
  brandId: number,
  countryIds: number[] | undefined,
  signal?: AbortSignal,
  denominationSort: DenominationSort = DEFAULT_DENOMINATION_SORT,
): Promise<BrandDetails> {
  return http.get<BrandDetails>(`/admin/brands/${brandId}`, {
    params: {
      countryIds,
      // Applied by the Admin API within each region; the response order is the display order.
      denominationSort: denominationSort.sort === 'name' ? 'name' : 'position',
      denominationDescending: denominationSort.direction === 'desc',
    },
    paramsSerializer: repeatArrayKeys,
    signal,
  });
}

/**
 * Every brand in storefront brand order. The response order is the display
 * order and is shown exactly as returned — never re-sorted here.
 */
export async function getBrandOrder(signal?: AbortSignal): Promise<BrandOrderItem[]> {
  return http.get<BrandOrderItem[]>('/admin/brands/order', { signal });
}

/**
 * Saves the storefront brand order (catalogue.manage, all-country scope): every
 * brand exactly once, first to last. The backend numbers them 1..n and audits
 * it; a stale or incomplete list is refused with 409 `BRAND_ORDER_INCOMPLETE`.
 */
export async function saveBrandOrder(brandIds: number[], reason: string): Promise<number> {
  const result = await http.put<{ changed: number }>('/admin/brands/order', { brandIds, reason });
  return result.changed;
}

/** The brand's denominations in one region, in storefront order, exactly as returned. */
export async function getDenominationOrder(
  brandId: number,
  regionId: number,
  signal?: AbortSignal,
): Promise<DenominationOrderItem[]> {
  return http.get<DenominationOrderItem[]>(`/admin/brands/${brandId}/denominations/order`, {
    params: { regionId },
    signal,
  });
}

/**
 * The same denominations ordered by the value in their names — a suggestion the
 * backend computes. Nothing is saved until the order is saved.
 */
export async function getDenominationOrderByValue(
  brandId: number,
  regionId: number,
  signal?: AbortSignal,
): Promise<DenominationOrderItem[]> {
  return http.get<DenominationOrderItem[]>(
    `/admin/brands/${brandId}/denominations/order/by-value`,
    { params: { regionId }, signal },
  );
}

/**
 * Saves the order of the brand's denominations in one region: every one exactly
 * once, first to last. 409 `DENOMINATION_ORDER_INCOMPLETE` when stale.
 */
export async function saveDenominationOrder(
  brandId: number,
  regionId: number,
  denominationIds: number[],
  reason: string,
): Promise<number> {
  const result = await http.put<{ changed: number }>(
    `/admin/brands/${brandId}/denominations/order`,
    { regionId, denominationIds, reason },
  );
  return result.changed;
}

/** Returns the new brand's id. */
export async function createBrand(payload: CreateBrandPayload): Promise<number> {
  return http.post<number>('/admin/brands', payload);
}

export async function updateBrand(brandId: number, payload: UpdateBrandPayload): Promise<void> {
  await http.put<number>(`/admin/brands/${brandId}`, payload);
}

/** What a change would do for customers in each country, before it is made. */
export async function getCatalogueImpact(
  brandId: number,
  request: CatalogueImpactRequest,
  signal?: AbortSignal,
): Promise<CatalogueImpact> {
  return http.post<CatalogueImpact>(`/admin/brands/${brandId}/impact`, request, { signal });
}

/** Returns how many records changed (0 when already in that state). */
export async function setBrandStatus(
  brandId: number,
  isActive: boolean,
  reason: string | null,
): Promise<number> {
  const result = await http.post<{ changed: number }>(`/admin/brands/${brandId}/status`, {
    isActive,
    reason,
  });
  return result.changed;
}

export async function setDenominationStatus(
  brandId: number,
  denominationIds: number[],
  isActive: boolean,
  reason: string | null,
): Promise<number> {
  const result = await http.post<{ changed: number }>(
    `/admin/brands/${brandId}/denominations/status`,
    { denominationIds, isActive, reason },
  );
  return result.changed;
}

/** Offer or withdraw denominations in one country. */
export async function setAvailability(
  brandId: number,
  countryId: number,
  denominationIds: number[],
  offered: boolean,
  reason: string | null,
): Promise<number> {
  const result = await http.post<{ changed: number }>(`/admin/brands/${brandId}/availability`, {
    countryId,
    denominationIds,
    offered,
    reason,
  });
  return result.changed;
}

/** Returns the new denomination's id. */
export async function createDenomination(
  brandId: number,
  payload: CreateDenominationPayload,
): Promise<number> {
  return http.post<number>(`/admin/brands/${brandId}/denominations`, payload);
}

export async function updateDenomination(
  brandId: number,
  denominationId: number,
  payload: DenominationPayload,
): Promise<void> {
  await http.put<number>(`/admin/brands/${brandId}/denominations/${denominationId}`, payload);
}

/** Whether the brand and each of its denominations can be deleted, and what blocks it. */
export async function getDeletionCheck(
  brandId: number,
  signal?: AbortSignal,
): Promise<CatalogueDeletionCheck> {
  return http.get<CatalogueDeletionCheck>(`/admin/brands/${brandId}/deletion-check`, { signal });
}

/**
 * Deletes a brand with all its denominations and their configuration. The
 * Admin API refuses it with 409 `CATALOGUE_IN_USE` when any denomination is
 * used by an order, cart, payment or customer (checked again under a lock).
 */
export async function deleteBrand(brandId: number, reason: string | null): Promise<void> {
  await http.delete<number>(`/admin/brands/${brandId}`, { data: { reason } });
}

/** Deletes one unused denomination with its configuration; 409 `CATALOGUE_IN_USE` otherwise. */
export async function deleteDenomination(
  brandId: number,
  denominationId: number,
  reason: string | null,
): Promise<void> {
  await http.delete<number>(`/admin/brands/${brandId}/denominations/${denominationId}`, {
    data: { reason },
  });
}

/** The same search as Excel (reports.export + catalogue.read). The backend records the export. */
export async function exportBrands(params: Omit<BrandListParams, 'page' | 'pageSize'>) {
  const { blob, fileName } = await http.download('/admin/brands/export', {
    params: {
      search: params.search,
      isActive: params.isActive,
      categoryId: params.categoryId,
      countryId: params.countryId,
      sort: (params.sort && SORTS[params.sort]) || 'name',
      descending: params.direction === 'desc',
      format: 'xlsx',
    },
  });
  return { blob, fileName: fileName ?? 'brands.xlsx' };
}
